<script setup lang="ts">
import {reactive, ref, watch, watchEffect} from "vue";
import {useToast} from "primevue/usetoast";
import User from "@/Models/User";
import {AutoComplete, Button, Dialog, InputText, Label} from "@/Components/Library";
import {storeConversation} from "@/Services/conversations";
import {getContacts as getContactsFromApi} from "@/Services/contacts";
import {Tag, User as UserType} from "@/Types";
import {watchDebounced} from "@vueuse/core";
import type {AutoCompleteCompleteEvent} from "primevue/autocomplete";
import {useRouter} from "vue-router";

const $user = new User();
const toast = useToast();
const router = useRouter();

const props = withDefaults(
    defineProps<{
        participants?: Array<UserType>;
        loading?: boolean;
    }>(),
    {
        participants: () => [],
        loading: false,
    },
);
const modelValue = defineModel<boolean | undefined>();

const state = reactive({
    loading: {
        conversation: props.loading,
    },
});

const emit = defineEmits(["update:modelValue"]);
const contacts = ref<Array<UserType>>([]);
const totalContacts = ref(0);
const searchQuery = ref("");

const handleSearch = (event: AutoCompleteCompleteEvent) => {
    searchQuery.value = event.query;
};

const newConversation = reactive({
    title: null as string | null,
    participants: [] as Array<UserType>,
    tags: [] as Array<Tag>,
});

const reset = () => {
    newConversation.title = null;
    newConversation.participants = [];
    newConversation.tags = [];
};

const getContacts = async () => {
    try {
        const {data, total} = await getContactsFromApi({
            active: true,
            filter: {
                search: searchQuery.value,
            },
        });
        contacts.value = data;
        console.log(contacts.value);
        totalContacts.value = total;
    } catch (errors) {
        console.log(errors);
    }
};

const createConversation = async () => {
    try {
        if (!newConversation.title || !newConversation.participants.length) {
            toast.add({
                severity: "warn",
                summary: "No participants selected",
                detail: "Please add a title and participants to create a chat.",
                life: 3000,
            });
            return;
        }
        state.loading.conversation = true;
        let formData = {
            ...newConversation,
            participants: newConversation.participants.map((P) => P.id),
        };

        const {data} = await storeConversation(formData);

        toast.add({
            severity: "success",
            summary: "Success",
            detail: "Chat has been created.",
            life: 3000,
        });

        await router.replace({name: "conversations.show", params: {conversation: data.id}});
        modelValue.value = false;
    } catch (errors) {
        console.log(errors);
    } finally {
        state.loading.conversation = false;
    }
};

watchEffect(async () => {
    reset();
    await getContacts();
});
watch(
    () => props.participants,
    () => (newConversation.participants = props.participants),
);
watchDebounced(searchQuery, getContacts, {debounce: 1000});
</script>
<template>
    <Dialog header="New chat" modal dismissable-mask v-model:visible="modelValue" class="w-full max-w-xl">
        <form @submit.prevent="createConversation">
            <div class="mb-6 flex flex-col">
                <Label for="title" class="mb-1" required>Chat title</Label>
                <InputText id="title" v-model="newConversation.title" required />
            </div>
            <div class="mb-6 flex flex-col">
                <Label for="participants" class="mb-1" required>Participants</Label>
                <AutoComplete
                    id="participants"
                    dropdown
                    dropdown-mode="current"
                    multiple
                    v-model="newConversation.participants"
                    :items="contacts.filter((P: UserType) => P.id !== $user.id)"
                    @complete="handleSearch"
                />
            </div>
            <!--            <TagSelect v-model="newConversation.tags" class="mb-8" />-->
            <div>
                <Button class="w-full bg-askr-green text-white" :loading="state.loading.conversation" type="submit">
                    Create
                </Button>
            </div>
        </form>
    </Dialog>
</template>
